/**
 * StaffOrderDisplay - Staff tablet display for managing all active orders
 * Shows both POS and Kiosk orders in a unified view for checkout counter staff
 * URL: /staff-display
 * Access: Requires PIN login with 'display', 'cashier', or 'owner' role
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, Coffee, CheckCircle, UtensilsCrossed, Bell,
  RefreshCw, User, CreditCard, Banknote, Monitor, ShoppingBag,
  AlertCircle, ChevronRight, Printer, XCircle, LogOut, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// PIN Pad Component for Display Login
const DisplayPINPad = ({ onLogin, loading }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleDigit = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');
      
      if (newPin.length === 6) {
        handleSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleSubmit = async (pinCode) => {
    const result = await onLogin(pinCode);
    if (!result.success) {
      setError(result.error || 'Invalid PIN');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 dark:from-black dark:via-purple-950 dark:to-black p-4" data-testid="display-login-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-600 rounded-2xl mb-4">
            <Monitor className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Counter Display</h1>
          <p className="text-purple-200">Enter your 6-digit PIN to sign in</p>
        </div>

        <div className="bg-purple-800/50 rounded-2xl p-6 mb-6 backdrop-blur">
          <div className="flex justify-center gap-3 mb-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${
                  pin.length > i
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-700 text-purple-400'
                }`}
              >
                {pin.length > i ? '•' : ''}
              </div>
            ))}
          </div>

          {error && (
            <div className="text-red-400 text-center text-sm mb-4 animate-shake">
              {error}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
              <button
                key={digit}
                onClick={() => handleDigit(digit.toString())}
                disabled={loading}
                className="h-16 bg-purple-700 hover:bg-purple-600 text-white text-2xl font-bold rounded-xl transition-colors"
              >
                {digit}
              </button>
            ))}
            <button
              onClick={handleClear}
              disabled={loading}
              className="h-16 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-xl transition-colors"
            >
              Clear
            </button>
            <button
              onClick={() => handleDigit('0')}
              disabled={loading}
              className="h-16 bg-purple-700 hover:bg-purple-600 text-white text-2xl font-bold rounded-xl transition-colors"
            >
              0
            </button>
            <button
              onClick={handleBackspace}
              disabled={loading}
              className="h-16 bg-purple-700 hover:bg-purple-600 text-white text-xl rounded-xl transition-colors"
            >
              ←
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center text-purple-300">
            Verifying...
          </div>
        )}
      </div>
    </div>
  );
};

export default function StaffOrderDisplay() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'kiosk', 'pos', 'server'
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'ready', 'completed'
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Authentication state
  const [staff, setStaff] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // Handle PIN login
  const handleLogin = async (pin) => {
    setLoginLoading(true);
    try {
      const res = await api.post('/api/device/login', { pin, device_type: 'display' });
      setStaff(res.data.staff);
      toast.success(res.data.message);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Invalid PIN' };
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    if (staff) {
      try {
        await api.post(`/api/pos/logout?staff_id=${staff.id}`);
      } catch (err) {
        console.error('Logout error:', err);
      }
    }
    setStaff(null);
  };

  // Fetch all active orders - MUST be defined before early return to follow React hooks rules
  const fetchOrders = useCallback(async () => {
    if (!staff) return; // Guard for when not logged in
    try {
      let statuses = 'pending,pending_confirmation,preparing,ready';
      if (statusFilter === 'ready') statuses = 'ready';
      if (statusFilter === 'completed') statuses = 'completed,picked_up';
      
      const res = await api.get(`/api/orders?status=${statuses}&limit=100`);
      let filteredOrders = res.data.orders || [];
      
      // Apply source filter
      if (filter !== 'all') {
        filteredOrders = filteredOrders.filter(o => o.order_type === filter);
      }
      
      // Sort by created_at (oldest first for FIFO)
      filteredOrders.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA - dateB;
      });
      
      setOrders(filteredOrders);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, staff]);

  useEffect(() => {
    if (!staff) return; // Guard for when not logged in
    fetchOrders();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchOrders, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchOrders, autoRefresh, staff]);

  // If not logged in, show PIN pad
  if (!staff) {
    return <DisplayPINPad onLogin={handleLogin} loading={loginLoading} />;
  }

  // Update order status
  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/api/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Order marked as ${newStatus}`);
      fetchOrders();
      setSelectedOrder(null);
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  // Print receipt
  const printReceipt = async (orderId) => {
    try {
      await api.post(`/api/receipts/print/${orderId}`, { type: 'customer' });
      toast.success('Receipt sent to printer');
    } catch (err) {
      toast.error('Print failed');
    }
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get wait time in minutes
  const getWaitTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - date) / 60000);
  };

  // Get source badge
  const getSourceBadge = (orderType) => {
    switch (orderType) {
      case 'kiosk':
        return { bg: 'bg-blue-600', text: 'KIOSK', icon: Monitor };
      case 'pos':
        return { bg: 'bg-teal-600', text: 'POS', icon: CreditCard };
      case 'server':
        return { bg: 'bg-purple-600', text: 'TABLE', icon: UtensilsCrossed };
      default:
        return { bg: 'bg-gray-600', text: 'ORDER', icon: ShoppingBag };
    }
  };

  // Get status styling
  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending_confirmation':
        return { bg: 'bg-red-500', text: 'NEEDS CONFIRM', animate: true };
      case 'pending':
        return { bg: 'bg-yellow-500', text: 'PENDING', animate: false };
      case 'preparing':
        return { bg: 'bg-orange-500', text: 'PREPARING', animate: false };
      case 'ready':
        return { bg: 'bg-green-500', text: 'READY', animate: false };
      default:
        return { bg: 'bg-gray-500', text: status?.toUpperCase(), animate: false };
    }
  };

  // Separate orders
  const pendingConfirmation = orders.filter(o => o.status === 'pending_confirmation');
  const activeOrders = orders.filter(o => ['pending', 'preparing'].includes(o.status));
  const readyOrders = orders.filter(o => o.status === 'ready');

  // Counts
  const kioskCount = orders.filter(o => o.order_type === 'kiosk').length;
  const posCount = orders.filter(o => o.order_type === 'pos').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex" data-testid="staff-order-display">
      {/* Left Panel - Order List */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-gray-900 p-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/logo.png" 
                alt="Bulaccino"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-lg font-bold">Staff Order Display</h1>
                <p className="text-gray-400 text-xs">{orders.length} orders</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Alerts */}
              {pendingConfirmation.length > 0 && (
                <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                  {pendingConfirmation.length} KIOSK NEEDS CONFIRM
                </div>
              )}

              {/* Filters */}
              <div className="flex bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 rounded text-xs font-medium ${
                    filter === 'all' ? 'bg-primary text-white' : 'text-gray-400'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('kiosk')}
                  className={`px-3 py-1 rounded text-xs font-medium ${
                    filter === 'kiosk' ? 'bg-blue-600 text-white' : 'text-gray-400'
                  }`}
                >
                  Kiosk ({kioskCount})
                </button>
                <button
                  onClick={() => setFilter('pos')}
                  className={`px-3 py-1 rounded text-xs font-medium ${
                    filter === 'pos' ? 'bg-teal-600 text-white' : 'text-gray-400'
                  }`}
                >
                  POS ({posCount})
                </button>
              </div>

              <Button variant="ghost" size="sm" onClick={fetchOrders}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              {/* Staff info and logout */}
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-700">
                <span className="text-sm text-gray-400">{staff?.name}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-red-400 hover:text-red-300"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Pending Confirmation Section */}
          {pendingConfirmation.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-bold text-red-400 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                KIOSK ORDERS - NEEDS CONFIRMATION ({pendingConfirmation.length})
              </h2>
              <div className="space-y-2">
                {pendingConfirmation.map(order => {
                  const source = getSourceBadge(order.order_type);
                  const waitTime = getWaitTime(order.created_at);
                  
                  return (
                    <button
                      key={order.id}
                      onClick={() => setSelectedOrder(order)}
                      className={`w-full bg-red-900/30 border-2 border-red-500 rounded-xl p-3 text-left hover:bg-red-900/50 transition-colors ${
                        selectedOrder?.id === order.id ? 'ring-2 ring-white' : ''
                      } ${waitTime > 2 ? 'animate-pulse' : ''}`}
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center text-lg font-bold">
                            #{order.order_number || order.id?.slice(0, 3)}
                          </div>
                          <div>
                            <p className="font-medium">{order.customer_name || 'Kiosk Guest'}</p>
                            <p className="text-xs text-gray-400">
                              {order.items?.length} items • ${order.total?.toFixed(2)} • {waitTime}m wait
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 ${source.bg} text-white text-xs rounded`}>
                          {source.text}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active Orders Section */}
          {activeOrders.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-bold text-orange-400 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                IN PROGRESS ({activeOrders.length})
              </h2>
              <div className="space-y-2">
                {activeOrders.map(order => {
                  const source = getSourceBadge(order.order_type);
                  const status = getStatusStyle(order.status);
                  const waitTime = getWaitTime(order.created_at);
                  
                  return (
                    <button
                      key={order.id}
                      onClick={() => setSelectedOrder(order)}
                      className={`w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-left hover:border-gray-500 transition-colors ${
                        selectedOrder?.id === order.id ? 'ring-2 ring-primary' : ''
                      }`}
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 ${status.bg} rounded-lg flex items-center justify-center text-lg font-bold`}>
                            #{order.order_number || order.id?.slice(0, 3)}
                          </div>
                          <div>
                            <p className="font-medium">{order.customer_name || 'Guest'}</p>
                            <p className="text-xs text-gray-400">
                              {order.items?.length} items • ${order.total?.toFixed(2)} • {waitTime}m
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 ${source.bg} text-white text-xs rounded`}>
                            {source.text}
                          </span>
                          <span className={`px-2 py-1 ${status.bg} text-white text-xs rounded`}>
                            {status.text}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ready Orders Section */}
          {readyOrders.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-green-400 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                READY FOR PICKUP ({readyOrders.length})
              </h2>
              <div className="space-y-2">
                {readyOrders.map(order => {
                  const source = getSourceBadge(order.order_type);
                  const waitTime = getWaitTime(order.created_at);
                  
                  return (
                    <button
                      key={order.id}
                      onClick={() => setSelectedOrder(order)}
                      className={`w-full bg-green-900/30 border border-green-700 rounded-xl p-3 text-left hover:bg-green-900/50 transition-colors ${
                        selectedOrder?.id === order.id ? 'ring-2 ring-green-400' : ''
                      }`}
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-lg font-bold">
                            #{order.order_number || order.id?.slice(0, 3)}
                          </div>
                          <div>
                            <p className="font-medium">{order.customer_name || 'Guest'}</p>
                            <p className="text-xs text-gray-400">
                              {order.items?.length} items • ${order.total?.toFixed(2)} • {waitTime}m total
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 ${source.bg} text-white text-xs rounded`}>
                          {source.text}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {orders.length === 0 && (
            <div className="text-center py-20">
              <Coffee className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500">No active orders</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Order Details */}
      <div className="w-96 bg-gray-900 border-l border-gray-800 flex flex-col">
        {selectedOrder ? (
          <>
            {/* Order Header */}
            <div className={`p-4 ${getSourceBadge(selectedOrder.order_type).bg}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-75">{getSourceBadge(selectedOrder.order_type).text} ORDER</p>
                  <p className="text-3xl font-bold">#{selectedOrder.order_number || selectedOrder.id?.slice(0, 4)}</p>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-white/10 rounded">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Customer Info */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium">{selectedOrder.customer_name || 'Guest'}</p>
                  <p className="text-xs text-gray-500">
                    {selectedOrder.customer_phone || selectedOrder.customer_email || 'No contact'}
                  </p>
                </div>
              </div>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-gray-400">
                  <Clock className="w-4 h-4" />
                  {formatTime(selectedOrder.created_at)}
                </span>
                <span className="flex items-center gap-1 text-green-400">
                  {selectedOrder.payment_method === 'card' ? (
                    <CreditCard className="w-4 h-4" />
                  ) : (
                    <Banknote className="w-4 h-4" />
                  )}
                  {selectedOrder.payment_status === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>
            </div>

            {/* Order Items */}
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Order Items</h3>
              <div className="space-y-3">
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="bg-gray-800 rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="font-medium">{item.quantity}x {item.name}</span>
                      <span>${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                    {item.selectedSize && (
                      <p className="text-xs text-gray-500 mt-1">Size: {item.selectedSize}</p>
                    )}
                    {item.modifiers?.length > 0 && (
                      <p className="text-xs text-blue-400 mt-1">
                        {item.modifiers.map(m => m.modifier_name || m.selected_option).join(', ')}
                      </p>
                    )}
                    {item.removedIngredients?.length > 0 && (
                      <p className="text-xs text-red-400 mt-1">
                        No: {item.removedIngredients.join(', ')}
                      </p>
                    )}
                    {item.extraIngredients?.length > 0 && (
                      <p className="text-xs text-green-400 mt-1">
                        Extra: {item.extraIngredients.map(e => e.name).join(', ')}
                      </p>
                    )}
                    {item.specialNotes && (
                      <p className="text-xs text-yellow-400 mt-1 bg-yellow-500/10 p-2 rounded">
                        Note: {item.specialNotes}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-4 pt-4 border-t border-gray-800">
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-green-400 text-sm mb-2">
                    <span>Discount</span>
                    <span>-${selectedOrder.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span>${selectedOrder.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-gray-800 space-y-2">
              {selectedOrder.status === 'pending_confirmation' && (
                <Button
                  onClick={() => updateStatus(selectedOrder.id, 'preparing')}
                  className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Confirm & Send Order
                </Button>
              )}
              {selectedOrder.status === 'preparing' && (
                <Button
                  onClick={() => updateStatus(selectedOrder.id, 'ready')}
                  className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg"
                >
                  <Bell className="w-5 h-5 mr-2" />
                  Mark Ready for Pickup
                </Button>
              )}
              {selectedOrder.status === 'ready' && (
                <Button
                  onClick={() => updateStatus(selectedOrder.id, 'completed')}
                  className="w-full bg-gray-600 hover:bg-gray-700 py-4 text-lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Complete Order
                </Button>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => printReceipt(selectedOrder.id)}
                  className="flex-1 border-gray-600"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Receipt
                </Button>
                {selectedOrder.status === 'pending_confirmation' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (confirm('Cancel this order?')) {
                        updateStatus(selectedOrder.id, 'cancelled');
                      }
                    }}
                    className="border-red-600 text-red-400 hover:bg-red-600/20"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select an order to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
